// components/ParamsCard/ResumeTable.tsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Spin, Tabs } from 'antd';
import Icon from '@ant-design/icons';
import { SearchOutlined } from '@ant-design/icons';
import ParameterIconTableSvg from '@/assets/svg/ParameterIconTableSvg';
import { generateColor } from '@/utils/DashboardGraphs';
import dayjs from 'dayjs';
import { LegendTable } from '@/components/Legend/LegendTable';
import { generateUsageColor } from '@/utils/generateUsageColor';

import {
	deleteReadingFromList,
	deleteResumeAndReadingToList,
} from '@/redux/slices/readingsResumesSlice';
import { TrashIconSvg } from '@/assets/svg';
import { useDashboardDataContext, clearGlobalSourcesInitialized } from '../DashboardDataContext';
import { useDispatch } from 'react-redux';
import { Button, DataGrid, DataGridColumn, Input, Tooltip } from '@hydrodigital/design-system';



const ResumeTable = ({
	resumeList,
	sourceList,
	isSpinnerResumeVisible = false,
	setIsReportModalOpen,

	toggleSeriesVisibility,
	setSourcesByParam,
	setSelectedParamsList,
	setDashboardContext,
}) => {
	const [queryParams, setQueryParams] = useState('');
	const [selectNav, setSelectNav] = useState<string>('parameter');
	const { selectedRowKeys, setSelectedRowKeys, seriesVisibilityState, setSeriesVisibilityState, setShowSources } =
		useDashboardDataContext();
	const previousDataRef = useRef(new Map());
	const dispatch = useDispatch();
	// No longer need to get clearSourcesInitialized from hook since we import it directly
    
	// 🐛 [BUG1-DEBUG] Track selectedRowKeys changes to detect unwanted deselections
	useEffect(() => {
		console.log('🔑 [ResumeTable] selectedRowKeys changed:', {
			keys: selectedRowKeys,
			count: selectedRowKeys.length,
			resumeListCount: resumeList?.length || 0,
		});
	}, [selectedRowKeys]);

	const legendInfo = useMemo(() => [
		{ label: 'Less than 0%', color: '#22a2ca' },
		{ label: '0% - 33%', color: '#34c38f' },
		{ label: '33% - 66%', color: '#ffd992' },
		{ label: '66% - 100%', color: '#e3a63d' },
		{ label: 'Greater than 100%', color: '#f46a6a' },
	], []);

	const filteredData = useMemo(() => {
		const data = !queryParams 
			? resumeList 
			: resumeList.filter((item) =>
				item.parameter?.toLowerCase().includes(queryParams.toLowerCase()),
			);
		
		// Add 'id' field to each item for DataGrid row identification
		// DataGrid's getRowId looks for 'id' field by default
		const dataWithId = data.map(item => ({
			...item,
			id: item.key // Map key to id for DataGrid
		}));
		
		return dataWithId;
	}, [queryParams, resumeList]);

	const columns: DataGridColumn[] = useMemo(
		() => [
			{
				id: 'graph',
				accessorKey: 'graph',
				header: 'Graph',
				width: 60,
				enableSorting: false,
				cell: ({ row }) => {
					const record = row.original;
					const color = generateColor(record.parameterId, null);
					return <ParameterIconTableSvg color={color} />;
				},
			},
			{
				id: 'infrastructure',
				header: 'Asset',
				accessorKey: 'infrastructure',
				cell: ({ row }) => {
					const record = row.original;
					return (
						<div className="flex column">
							<span>{record.infrastructureName}</span>
							<span style={{ color: '#74788D', fontSize: '10px' }}>{record.infrastructureType}</span>
						</div>
					);
				},
			},
			{ 
				id: 'parameter',
				header: 'Parameter', 
				accessorKey: 'parameter',
				cell: ({getValue}) => <span>{getValue()}</span>,
			},
			{
				id: 'currentValue',
				header: 'Last Value',
				accessorKey: 'currentValue',
				cell: ({ row }) => {
					const record = row.original;
					return <span>{record.currentValue !== null ? `${record.currentValue} ${record.unit}` : 'No data'}</span>;
				},
			},
			{
				id: 'usage',
				header: 'Capacity',
				accessorKey:'usage',
				cell: ({ row }) => {
					const record = row.original;
					return (
						<div className="flex items-center gap-2 overflow-hidden">
							{ record.usage !== null ? <div
								className="w-3 h-3 rounded-full"
								style={{ backgroundColor: generateUsageColor(record.usage) }}
							 /> : null}
							
							<Tooltip title={record.usage !== null ? `${record.usage > 0 ? record.usage.toFixed(2) : record.usage} %` : 'No data'}>
								<span className="overflow-hidden whitespace-nowrap text-ellipsis w-20">{record.usage !== null ? `${record.usage > 0 ? record.usage.toFixed(2) : record.usage} %` : 'No data'}</span>
							</Tooltip>
						</div>
					);
				},
			},
			{
				id: 'lastCollected',
				header: 'Last Collected',
				accessorKey: 'lastCollected',
				cell: ({ row }) => {
					const record = row.original;
					return (
						<span>
							{record.lastCollected && record.lastCollected !== 'N/A'
								? dayjs(record.lastCollected).format('YYYY/MM/DD hh:mm A')
								: 'No data available'}
						</span>
					);
				},
			},
			{
				id: 'actions',
				header: '',
				width: 60,
				enableSorting: false,
				cell: ({ row }) => {
					const record = row.original;
					return (
						<Tooltip text="Remove" trigger='hover' position='up' minWidth={95}>
							<span>
							<Icon
								component={() => <TrashIconSvg color="#74788D" />}
								style={{ color: '#74788D', fontSize: '20px', cursor: 'pointer' }}
								onClick={() => {
									setSourcesByParam((oldValue) => {
										const filtered = oldValue.filter((sp) => sp.id != record.parameterId);
										return filtered;
									});
									setSelectedParamsList((selectedParams) => {
										let copy = [...selectedParams];
										const paramToDelete = copy.find((param) => {
											return param.parameterId == record.parameterId;
										});
										if (paramToDelete) {
											paramToDelete.sourceList.forEach((source) => {
												if (source.sourceId) {
													dispatch(
														deleteReadingFromList(
															paramToDelete.parameterId,
															source.serialNumber,
														) as any,
													);
												} else {
													dispatch(
														deleteResumeAndReadingToList(paramToDelete.parameterId, null) as any,
													);
												}
											});
										}
										copy = copy.filter((param) => param.parameterId != record.parameterId);
										return copy;
									});

									setDashboardContext((currentContext) => {
										const newSelectedSensors = currentContext.selectedSensors.filter((sensor) => {
											return sensor.measurementTargetId != record.parameterId;
										});
										if (newSelectedSensors.length === 0) {
											setShowSources(false);
										}
										return {
											...currentContext,
											isExpandedParamsCard: false,
											selectedSensors: newSelectedSensors,
										};
									});
								}}
							/>
							</span>
						</Tooltip>
					);
				},
			},
		],
		[seriesVisibilityState, toggleSeriesVisibility, setSourcesByParam, setSelectedParamsList, setDashboardContext, dispatch],
	);
	
	const onSelectInfrastructure = useCallback((record: any, selected: boolean, selectedRows: any[]) => {
		if (selected) {
			// Parameter selected: show recommended source in chart
			toggleSeriesVisibility(record);
		} else {
			// Parameter deselected: handle chart cleanup
			setSourcesByParam((currentSources) => {
				const paramSources = currentSources.find(ps => ps.id === record.parameterId);
				if (paramSources) {
					// Remove sources from chart - hide recommended, delete others
					paramSources.sources.forEach((source) => {
						if (source.checked) {
							if (source.sourceId === null && source.serialNumber === null) {
								// For recommended source, hide it using toggleSeriesVisibility
								toggleSeriesVisibility(record);
							} else {
								// For regular sources, delete them from chart
								dispatch(deleteReadingFromList(record.parameterId, source.serialNumber) as any);
							}
						}
					});
				}
				return currentSources;
			});
		}

		// Update sources state based on parameter selection
		setSourcesByParam((currentSources) => {
			const updatedSources = currentSources.map((paramSource) => {
				if (paramSource.id === record.parameterId) {
					if (selected) {
						// Parameter selected: enable sources and check only recommended
						const updatedParamSource = paramSource.sources.map((source) => ({
							...source,
							checked: source.name === 'Recommended' && source.sourceId === null,
						}));
						return {
							...paramSource,
							sources: updatedParamSource,
						};
					} else {
						// Parameter deselected: uncheck all sources
						const updatedParamSource = paramSource.sources.map((source) => ({
							...source,
							checked: false,
						}));
						return {
							...paramSource,
							sources: updatedParamSource,
						};
					}
				}
				return paramSource;
			});
			
			return updatedSources;
		});

		// Clear sourcesInitialized AFTER all re-renders complete to prevent re-initialization
		if (!selected) {
			setTimeout(() => {
				clearGlobalSourcesInitialized(record.parameterId);
			}, 0);
		}

		// Update selected row keys using functional update to avoid dependency on current selectedRowKeys
		setSelectedRowKeys((currentKeys) => {
			const key = record.key;
			const newKeys = selected
				? [...currentKeys, key]
				: currentKeys.filter((k) => k !== key);
			
			return newKeys;
		});
	}, [toggleSeriesVisibility, seriesVisibilityState, setSeriesVisibilityState, setSourcesByParam, setSelectedParamsList, setDashboardContext, dispatch]);

	// Convert selectedRowKeys array to RowSelectionState object (Record<string, boolean>)
	const rowSelectionState = useMemo<Record<string, boolean>>(() => {
		const selectionObj: Record<string, boolean> = {};
		selectedRowKeys.forEach((key) => {
			const stringKey = String(key);
			selectionObj[stringKey] = true;
		});
		
		return selectionObj;
	}, [selectedRowKeys]);

	// Handle row selection changes from DataGrid (TanStack Table OnChangeFn)
	// The DataGrid updater may not preserve previously selected rows in the returned object,
	// so we detect the single toggled row by comparing old vs new selection state.
	const handleRowSelectionChange = useCallback((updaterOrValue: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)) => {
		const newSelection = typeof updaterOrValue === 'function' 
			? updaterOrValue(rowSelectionState) 
			: updaterOrValue;
		
		// Find which key was toggled by comparing old and new selection
		const oldKeys = new Set(Object.keys(rowSelectionState).filter(k => rowSelectionState[k]));
		const newKeys = new Set(Object.keys(newSelection).filter(k => newSelection[k]));

		// Detect the single added or removed key
		let addedKey: string | null = null;
		let removedKey: string | null = null;

		for (const k of newKeys) {
			if (!oldKeys.has(k)) {
				addedKey = k;
				break;
			}
		}
		for (const k of oldKeys) {
			if (!newKeys.has(k)) {
				removedKey = k;
				break;
			}
		}

		// Process only the single toggled row
		if (addedKey) {
			const record = filteredData.find(item => String(item.key) === String(addedKey));
			if (record) {
				onSelectInfrastructure(record, true, []);
			}
		}
		
		if (removedKey) {
			const record = filteredData.find(item => String(item.key) === String(removedKey));
			if (record) {
				onSelectInfrastructure(record, false, []);
			}
		}
	}, [rowSelectionState, filteredData, onSelectInfrastructure]);
	
	const tabsOnChange = useCallback((key: string) => setSelectNav(key), []);

	const tabsItems = useMemo(() => [
		{ key: 'parameter', label: 'PARAMETERS' },
		{ key: 'source', label: 'SOURCES' },
	], []);

	return (
		<>
			{isSpinnerResumeVisible ? (
				<div className="spinner-container">
					<Spin tip="Processing data..." />
				</div>
			) : (
				<>
					<div
						className="flex justify-center items-center"
						style={{ position: 'absolute', top: '10px', right: '10px' }}
					 />
					<div className="flex items-center justify-between gap-2 mb-1">
						<div className="flex items-center gap-2 min-w-0 flex-1">
							<span
								className="shrink-0"
								style={{
									fontWeight: 'bold',
									color: '#023E65',
									textTransform: 'uppercase',
									fontSize: '13px',
								}}
							>
								Parameter summary
							</span>
							<div className="flex-1" style={{ maxWidth: '200px', minWidth: '100px' }}>
								<Input
									placeholder="Search Parameters"
									leftIcon={<SearchOutlined />}
									size='small'
									onChange={(e) => setQueryParams(e.target.value)}
								/>
							</div>
						</div>
						<Tooltip text="Download report" trigger='hover' position='up'>
							<span>
								<Button
									className="shrink-0"
									variant="primary"
									size='small'
									showLabel={false}
									onClick={() => {
										setIsReportModalOpen(true);
									}}
									iconName='download'
								/>
							</span>
						</Tooltip>
					</div>
					<Tabs
						activeKey={selectNav}
						onChange={tabsOnChange}
						items={tabsItems}
					/>
					{selectNav === 'parameter' ? (
						<div className="flex flex-col w-full items-center gap-2 overflow-auto">
							<div className="flex w-full justify-end mt-2 mb-1">
								<LegendTable data={legendInfo} fontSize="small" />
							</div>
							<DataGrid
								columns={columns}
								data={filteredData}
								className="w-full"
								pagination={{ enabled: false }}
								selection={{ 
									enabled: true, 
									mode: 'multiple',
									showSelectAll: false
								}}
								rowSelection={rowSelectionState}
								onRowSelectionChange={handleRowSelectionChange}
								emptyMessage={
									<div className="flex column">
										<span>No data: select some data to turn on the light</span>
									</div>
								}
								striped={true}
								hoverable={true}
								bordered={true}
							/>
						</div>
					) : (
						<div className="flex flex-col gap-2 overflow-auto">{sourceList}</div>
					)}
				</>
			)}
		</>
	);
};

export default React.memo(ResumeTable);
